import { useState } from "react";

import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import GiftDialog from "@/components/zemelapis/GiftDialog";
import { Button } from "@/components/ui/button";
import { ZEMELAPIS_TIERS, zemelapisProduct } from "@/lib/zemelapisTiers";
import { cn } from "@/lib/utils";

interface Props {
  /** "dark" = placed on a terracotta / ink card, "light" = on cream or white */
  tone?: "dark" | "light";
  className?: string;
}

/**
 * Three compact SOLO Žemėlapis tiers, one row each. Every row opens the express
 * digital checkout with only that tier in it — the stored cart is untouched.
 */
const ZemelapisTierRows = ({ tone = "light", className }: Props) => {
  const { openExpressCheckout } = useCart();
  const { user } = useAuth();
  const [giftDialogOpen, setGiftDialogOpen] = useState(false);

  return (
    <div className={cn("grid gap-2", className)}>
      {ZEMELAPIS_TIERS.map((t) => (
        <div
          key={t.tier}
          className={cn(
            "grid min-h-14 grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-full px-4 py-2",
            tone === "dark" ? "bg-cream/[0.12] border border-cream/25" : "bg-cream border border-ink/[0.12]"
          )}
        >
          <div className="min-w-0 flex items-center gap-2 flex-wrap">
            <span className={cn("text-sm font-medium truncate", tone === "dark" ? "text-cream" : "text-ink")}>
              €{t.price.toFixed(2)} · {t.label}
            </span>
            {t.best && (
              <span
                className={cn(
                  "text-[9px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full",
                  tone === "dark" ? "bg-cream text-terracotta" : "bg-terracotta text-cream"
                )}
              >
                Geriausia
              </span>
            )}
          </div>
          <Button
            type="button"
            onClick={() => {
              // "Dovana" needs the personalisation dialog before checkout.
              if (t.tier === "gift_single") { setGiftDialogOpen(true); return; }
              openExpressCheckout(zemelapisProduct(t.tier));
            }}
            className={cn(
              "h-9 flex-shrink-0 rounded-full px-5 text-xs font-bold tracking-[0.1em] uppercase",
              tone === "dark"
                ? "bg-cream text-terracotta hover:bg-cream/90"
                : "bg-terracotta text-cream hover:bg-terracotta/90"
            )}
          >
            Pirkti
          </Button>
        </div>
      ))}
      <GiftDialog
        open={giftDialogOpen}
        onOpenChange={setGiftDialogOpen}
        tier="gift_single"
        defaultSenderName={user?.user_metadata?.full_name || ""}
        onConfirm={(fields) => openExpressCheckout(zemelapisProduct("gift_single", fields))}
      />
    </div>
  );
};

export default ZemelapisTierRows;
